import { useEffect, useMemo, useState } from 'react';
import { Text, View } from 'react-native';

import { Button, Sheet, TextField, Select, type SelectOption } from '@/components/ui';
import { upsertProductoVenta, upsertInsumoInterno } from '@/services/firebase/inventario';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { proveedoresQuery } from '@/services/firebase/proveedores';
import { colors, typography } from '@/theme/tokens';
import { dateToDDMMYYYY, formatearFechaInput, parsearFechaDDMMYYYY } from '@/utils/format';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

type Tipo = 'venta' | 'interno';

interface Props {
  visible: boolean;
  onClose: () => void;
  tipo: Tipo;
  producto: ProductoVenta | InsumoInterno | null;
}

export function ProductoSheet({ visible, onClose, tipo, producto }: Props) {
  const { data: proveedores } = useFirestoreCollection(proveedoresQuery);

  const [nombre, setNombre] = useState('');
  const [presentacion, setPresentacion] = useState('');
  const [categoria, setCategoria] = useState('');
  const [proveedorId, setProveedorId] = useState<string | null>(null);
  const [stock, setStock] = useState('0');
  const [stockMin, setStockMin] = useState('1');
  const [precioCompra, setPrecioCompra] = useState('0');
  const [precioVenta, setPrecioVenta] = useState('0');
  const [vencimiento, setVencimiento] = useState(''); // DD/MM/YYYY
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setNombre(producto?.nombre ?? '');
    setPresentacion(producto?.presentacion ?? '');
    setCategoria(producto?.categoria ?? '');
    setProveedorId(producto?.proveedorId ?? null);
    setStock(String(producto?.stock ?? 0));
    setStockMin(String(producto?.stockMin ?? 1));
    setPrecioCompra(String((producto as ProductoVenta | null)?.precioCompra ?? 0));
    setPrecioVenta(String((producto as ProductoVenta | null)?.precioVenta ?? 0));
    setVencimiento(
      producto?.vencimiento?.toDate ? dateToDDMMYYYY(producto.vencimiento.toDate()) : '',
    );
    setNotas(producto?.notas ?? '');
  }, [visible, producto]);

  const proveedorOptions = useMemo<SelectOption[]>(
    () => proveedores.map((p) => ({ value: p.id, label: p.nombre, hint: p.whatsapp })),
    [proveedores],
  );

  const onSave = async () => {
    setError(null);
    if (!nombre.trim()) return setError('El nombre es obligatorio.');
    if (!presentacion.trim()) return setError('La presentación es obligatoria.');
    const stockN = Number(stock);
    const stockMinN = Number(stockMin);
    if (Number.isNaN(stockN) || stockN < 0) return setError('Stock inválido.');
    if (Number.isNaN(stockMinN) || stockMinN < 0) return setError('Stock mínimo inválido.');

    let venc: Date | null = null;
    if (vencimiento.trim()) {
      const d = parsearFechaDDMMYYYY(vencimiento);
      if (!d) return setError('Fecha de vencimiento inválida. Usá DD/MM/AAAA.');
      venc = d;
    }

    const prov = proveedores.find((p) => p.id === proveedorId);

    setSaving(true);
    try {
      const base = {
        id: producto?.id,
        nombre: nombre.trim(),
        presentacion: presentacion.trim(),
        categoria: categoria.trim() || undefined,
        proveedorId: proveedorId ?? null,
        proveedorNombre: prov?.nombre ?? null,
        proveedorWhatsapp: prov?.whatsapp ?? null,
        stock: stockN,
        stockMin: stockMinN,
        vencimiento: venc,
        notas: notas.trim() || undefined,
      };
      if (tipo === 'venta') {
        const pCompra = Number(precioCompra);
        if (Number.isNaN(pCompra) || pCompra < 0) return setError('Precio de compra inválido.');
        await upsertProductoVenta({ ...base, precioCompra: pCompra, precioVenta: 0 });
      } else {
        await upsertInsumoInterno(base);
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="lg"
      title={producto ? 'Editar producto' : tipo === 'venta' ? 'Nuevo producto a la venta' : 'Nuevo insumo interno'}
      subtitle={
        tipo === 'venta'
          ? 'Stock comercial. Las alertas de stock bajo abrirán WhatsApp del proveedor.'
          : 'Insumo de uso clínico. Los doctores pueden descontar al usar.'
      }
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button label={producto ? 'Guardar cambios' : 'Crear'} onPress={onSave} loading={saving} />
        </>
      }
    >
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField containerStyle={{ flex: 2, minWidth: 240 }} label="Nombre" placeholder="Amoxican" value={nombre} onChangeText={setNombre} />
        <TextField containerStyle={{ flex: 1, minWidth: 160 }} label="Presentación" placeholder="Jarabe / Caja / Pipeta" value={presentacion} onChangeText={setPresentacion} />
      </View>

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField containerStyle={{ flex: 1, minWidth: 200 }} label="Categoría" placeholder="Antibiótico / Corticoide…" value={categoria} onChangeText={setCategoria} />
        <Select
          containerStyle={{ flex: 1, minWidth: 200 }}
          label="Proveedor"
          value={proveedorId}
          options={proveedorOptions}
          onChange={setProveedorId}
          placeholder="Sin asignar"
          allowClear
        />
      </View>

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField containerStyle={{ flex: 1, minWidth: 140 }} label="Stock actual" value={stock} onChangeText={setStock} keyboardType="numeric" />
        <TextField containerStyle={{ flex: 1, minWidth: 140 }} label="Stock mínimo" value={stockMin} onChangeText={setStockMin} keyboardType="numeric" hint="Si stock ≤ mínimo, se dispara alerta." />
        <TextField
          containerStyle={{ flex: 1, minWidth: 160 }}
          label="Vencimiento"
          placeholder="DD/MM/AAAA"
          value={vencimiento}
          onChangeText={(t) => setVencimiento(formatearFechaInput(t))}
          keyboardType="numeric"
          hint="Opcional. Formato día/mes/año."
        />
      </View>

      {tipo === 'venta' ? (
        <TextField
          label="Precio de compra (Bs)"
          value={precioCompra}
          onChangeText={setPrecioCompra}
          keyboardType="decimal-pad"
          hint="Cuánto te costó. No se muestra a doctores ni recepción."
        />
      ) : null}

      <TextField label="Notas" placeholder="Ubicación, observaciones…" value={notas} onChangeText={setNotas} multiline />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
